import React, { useState } from 'react';
import { 
  FileText, History, Users, Settings, LogOut, 
  Building2, Menu, X, ChevronDown, UserCircle
} from 'lucide-react';
import { useAuth } from '../Auth/AuthContext';

export default function Layout({ children, view, setView }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const navItems = [
    { id: 'new-quote', label: 'Ny offert', icon: FileText },
    { id: 'quotes', label: 'Offerter', icon: History },
    { id: 'customers', label: 'Kunder', icon: Users },
    { id: 'company', label: 'Företagsprofil', icon: Building2 },
    ...(user?.role === 'admin' ? [{ id: 'team', label: 'Team', icon: Users }] : []),
  ];

  const NavButton = ({ item }) => (
    <button
      onClick={() => { setView(item.id); setMobileOpen(false); }}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all w-full text-left
        ${view === item.id || (item.id === 'new-quote' && ['notes','builder','preview'].includes(view))
          ? 'bg-blue-600 text-white shadow-md' 
          : 'text-slate-600 hover:bg-slate-100'}`}
    >
      <item.icon size={18} />
      {item.label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar – desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 fixed top-0 left-0 h-full p-4 z-10">
        <div 
          className="flex items-center gap-2 mb-8 cursor-pointer px-2"
          onClick={() => setView('new-quote')}
        >
          <div className="bg-blue-600 p-2 rounded-lg text-white">
            <FileText size={20} />
          </div>
          <span className="font-black text-xl tracking-tighter">iOffer</span>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map(item => <NavButton key={item.id} item={item} />)}
        </nav>

        {/* Användarprofil */}
        <div className="border-t border-slate-100 pt-4 mt-4">
          <div className="px-2 mb-2">
            <p className="text-xs font-bold text-slate-400 uppercase truncate">{user?.tenant_name}</p>
            <p className="text-sm font-bold truncate">{user?.email}</p>
            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${user?.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
              {user?.role === 'admin' ? 'Admin' : 'Medlem'}
            </span>
          </div>
          <button
            onClick={() => { setView('profile'); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 rounded-lg"
          >
            <UserCircle size={16} /> Min profil
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg"
          >
            <LogOut size={16} /> Logga ut
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4 z-20">
        <div className="flex items-center gap-2">
          <div className="bg-blue-600 p-1.5 rounded-lg text-white"><FileText size={16} /></div>
          <span className="font-black text-lg tracking-tighter">iOffer</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-10 bg-white pt-14 p-4">
          <nav className="space-y-1">
            {navItems.map(item => <NavButton key={item.id} item={item} />)}
            <button
              onClick={() => { setView('profile'); setMobileOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 w-full"
            >
              <UserCircle size={18} /> Min profil
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 w-full"
            >
              <LogOut size={18} /> Logga ut
            </button>
          </nav>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 md:ml-64 pt-14 md:pt-0 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
